import { ButtonV2, Dropdown } from '@cimplico/components';
import { useState } from 'react';
import { ArrowSmallLeftIcon, CheckIcon, EllipsisHorizontalIcon, PencilIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { Journal, JournalStatusEnum, JournalTaxTypeEnum } from '../../types/journal';
import { useDeleteJournals } from '../../queries/use-delete-journals';
import { toastNotification } from '../../components/toast/Toast';
import { isNullOrUndefined } from '../new-workpaper/utils';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import { WorkpaperRoleEnum } from '../../types/workpaper';
import JournalDetails from './JournalDetails';
import JournalLinesTable from './JournalLinesTable';
import DeleteJournalsConfirmDialog from './DeleteJournalsConfirmDialog';
import VoidJournalsConfirmDialog from './VoidJournalsConfirmDialog';
import PostJournalsConfirmDialog from '../journals/PostJournalsConfirmDialog';
import useWindowResize from '../../hooks/use-window-resize';

const MD_SCREEN_SIZE = 768;

function isPostingDisabled(journal: Journal): boolean {
    const isImbalanceJournal = journal.total?.credit !== journal.total?.debit;
    const isMissingJournalFields = !journal.narration || !journal.date || journal.lines.length < 2 || !journal.taxType;
    const isIncompleteLineFields = journal.lines.some(
        (l) => !l.description || !l.account || (journal.taxType !== JournalTaxTypeEnum.NO_TAX && isNullOrUndefined(l.lineAmount))
    );

    if (isImbalanceJournal || isMissingJournalFields || isIncompleteLineFields) {
        return true;
    }

    return false;
}

type JournalOverviewProps = {
    useSmallerStyling?: boolean;
    journal: Journal;
    worksheetId?: string;
    onClose: () => void;
    onEdit: () => void;
    onXeroError: (error?: { name: string; message: string }) => void;
};

function JournalOverview({
    useSmallerStyling = false,
    journal,
    worksheetId,
    onClose,
    onEdit,
    onXeroError,
}: JournalOverviewProps): JSX.Element {
    const [action, setAction] = useState<string | undefined>(undefined);
    const { readOnly, role, permissions } = useWorkpaperContext();
    const { width } = useWindowResize();

    const roleCanPost =
        role === WorkpaperRoleEnum.REVIEWER ||
        role === WorkpaperRoleEnum.APPROVER ||
        (role === WorkpaperRoleEnum.PREPARER && permissions.preparer.canPostJournal);

    const deleteJournalsQuery = useDeleteJournals(worksheetId);

    function deleteJournals(): void {
        deleteJournalsQuery.mutate(
            { ids: [journal.id] },
            {
                onSuccess: () => {
                    onClose();
                    toastNotification('success', `1 draft journal deleted!`);
                },
            }
        );
    }

    return (
        <>
            <PostJournalsConfirmDialog
                journalIds={[journal.id]}
                worksheetId={worksheetId}
                isOpen={action === 'POST'}
                onXeroError={onXeroError}
                onClose={(): void => setAction(undefined)}
                onPost={(): void => setAction(undefined)}
            />
            {action === 'DELETE' && (
                <DeleteJournalsConfirmDialog journalIds={[journal.id]} onClose={(): void => setAction(undefined)} onDelete={onClose} />
            )}
            {action === 'VOID' && (
                <VoidJournalsConfirmDialog
                    worksheetId={worksheetId}
                    journalIds={[journal.id]}
                    onXeroError={onXeroError}
                    onClose={(): void => setAction(undefined)}
                    onVoid={(): void => setAction(undefined)}
                />
            )}
            <div className="flex flex-col gap-2 pb-4">
                <div className="flex gap-2 items-center text-gray-500 cursor-pointer w-fit" onClick={onClose}>
                    <ArrowSmallLeftIcon className="h-5 w-5" />
                    <div className={`${useSmallerStyling ? 'text-xs' : 'text-sm'}`}>Back to journals</div>
                </div>
                <div className="flex justify-between items-center">
                    <h1
                        className={`${
                            useSmallerStyling ? 'text-lg' : 'text-2xl'
                        } leading-none text-gray-800 font-bold dark:text-white/[0.87] max-w-[50ch]`}
                    >
                        JNL#{journal.reference.toString().padStart(3, '0')}
                        {journal.narration ? ` - ${journal.narration}` : ''}
                    </h1>
                    {journal.status !== JournalStatusEnum.VOIDED &&
                        journal.status !== JournalStatusEnum.ARCHIVED &&
                        width > MD_SCREEN_SIZE && (
                            <div className="flex gap-4">
                                {journal.status === JournalStatusEnum.DRAFT && (
                                    <ButtonV2
                                        type="danger"
                                        label="Delete"
                                        size="xs"
                                        leftIcon={<XMarkIcon />}
                                        onClick={deleteJournals}
                                        isDisabled={readOnly}
                                    />
                                )}
                                {journal.status === JournalStatusEnum.POSTED && (
                                    <ButtonV2
                                        type="danger"
                                        label="Void"
                                        size="xs"
                                        leftIcon={<XMarkIcon />}
                                        onClick={(): void => setAction('VOID')}
                                        isDisabled={readOnly || !roleCanPost}
                                        disabledHint={!roleCanPost ? 'This requires a reviewer role.' : undefined}
                                    />
                                )}
                                <ButtonV2
                                    type="secondary"
                                    label="Edit"
                                    size="xs"
                                    leftIcon={<PencilIcon />}
                                    onClick={onEdit}
                                    isDisabled={readOnly || (journal.status === JournalStatusEnum.POSTED && !roleCanPost)}
                                    disabledHint={
                                        journal.status === JournalStatusEnum.POSTED && !roleCanPost
                                            ? 'This requires a reviewer role.'
                                            : undefined
                                    }
                                />
                                {journal.status === JournalStatusEnum.DRAFT && (
                                    <ButtonV2
                                        type="primary"
                                        label="Post"
                                        size="xs"
                                        leftIcon={<CheckIcon />}
                                        onClick={(): void => setAction('POST')}
                                        isDisabled={isPostingDisabled(journal) || readOnly || !roleCanPost}
                                        disabledHint={!roleCanPost ? 'This requires a reviewer role.' : undefined}
                                    />
                                )}
                            </div>
                        )}
                    {journal.status !== JournalStatusEnum.VOIDED &&
                        journal.status !== JournalStatusEnum.ARCHIVED &&
                        width < MD_SCREEN_SIZE && (
                            <Dropdown type="secondary" rightIcon={<EllipsisHorizontalIcon />} size="xs" disabled={readOnly}>
                                {journal.status === JournalStatusEnum.DRAFT && (
                                    <Dropdown.Option label="Delete" onClick={deleteJournals} leftIcon={<XMarkIcon />} />
                                )}
                                {journal.status === JournalStatusEnum.POSTED && (
                                    <Dropdown.Option
                                        label="Void"
                                        onClick={(): void => setAction('VOID')}
                                        leftIcon={<XMarkIcon />}
                                        disabled={!roleCanPost}
                                        disabledHint={!roleCanPost ? 'This requires a reviewer role.' : undefined}
                                    />
                                )}
                                <Dropdown.Option
                                    label="Edit Details"
                                    onClick={onEdit}
                                    leftIcon={<PencilIcon />}
                                    disabled={readOnly || (journal.status === JournalStatusEnum.POSTED && !roleCanPost)}
                                    disabledHint={
                                        journal.status === JournalStatusEnum.POSTED && !roleCanPost
                                            ? 'This requires a reviewer role.'
                                            : undefined
                                    }
                                />
                                {journal.status === JournalStatusEnum.DRAFT && (
                                    <Dropdown.Option
                                        label="Post Journal"
                                        onClick={(): void => setAction('POST')}
                                        disabledHint={!roleCanPost ? 'This requires a reviewer role.' : undefined}
                                        disabled={isPostingDisabled(journal) || !roleCanPost}
                                        leftIcon={<CheckIcon />}
                                    />
                                )}
                            </Dropdown>
                        )}
                </div>
                <JournalDetails showTaxExclAmount showStatus journal={journal} />
            </div>
            <JournalLinesTable journal={journal} />
        </>
    );
}

export default JournalOverview;
